import { useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useGuideline } from '../../context/GuidelineContext';
import { toFlowNodes, toFlowEdges } from '../../utils/graphUtils';
import { findGuidelineForPage } from '../../utils/guidelineLoader';
import { nodeTypes } from './nodes';
import PageSelector from './PageSelector';
import NodeDetailPanel from './NodeDetailPanel';

function FlowchartCanvas() {
  const {
    pageNodes,
    pageEdges,
    pages,
    activePage,
    setPage,
    guideline,
    guidelineId,
    selectedNodeId,
    setSelectedNodeId,
    unknownPage,
    requestedPageId,
  } = useGuideline();
  const { fitView } = useReactFlow();
  const navigate = useNavigate();

  const nodes = useMemo(() => toFlowNodes(pageNodes), [pageNodes]);
  const edges = useMemo(() => toFlowEdges(pageEdges), [pageEdges]);

  // The selected node lives in the URL so a specific decision point is linkable.
  const selectedNode = useMemo(
    () => pageNodes.find((n) => n.id === selectedNodeId) || null,
    [pageNodes, selectedNodeId]
  );

  // Only re-fit when the page's graph actually changes. Re-fitting when the
  // detail panel opens or closes threw away wherever the user had panned to.
  useEffect(() => {
    const t = setTimeout(() => fitView({ padding: 0.3 }), 150);
    return () => clearTimeout(t);
  }, [nodes, edges, fitView]);

  const onNodeClick = useCallback(
    (_event, node) => setSelectedNodeId(node.id),
    [setSelectedNodeId]
  );

  const handleReferenceNavigate = useCallback(
    (targetPage) => {
      const owner = findGuidelineForPage(targetPage);
      if (owner && owner !== guidelineId) {
        // Cross-guideline reference: switch guideline rather than setting a page
        // this guideline doesn't have, which used to render a blank canvas.
        navigate(`/guidelines/${owner}?page=${encodeURIComponent(targetPage)}`);
        return;
      }
      setPage(targetPage);
    },
    [setPage, navigate, guidelineId]
  );

  return (
    <>
      <PageSelector pages={pages} activePage={activePage} onPageChange={setPage} />
      {unknownPage && (
        <div className="guideline-validation-notice" role="status">
          Page “{requestedPageId}” is not part of {guideline?.name}. Showing{' '}
          {activePage} instead.
        </div>
      )}
      <div
        className="flowchart-canvas-wrap"
        style={{
          width: selectedNode ? 'calc(100% - 372px)' : '100%',
          height: 'calc(100dvh - 240px)',
          minHeight: '500px',
          transition: 'width 0.2s ease-out',
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          minZoom={0.2}
          maxZoom={2.5}
          defaultEdgeOptions={{ type: 'smoothstep' }}
          style={{ width: '100%', height: '100%' }}
        >
          <Background color="#e2e8f0" gap={20} />
          <Controls position="bottom-right" />
        </ReactFlow>
      </div>
      {selectedNode && (
        <NodeDetailPanel
          node={selectedNode}
          footnotes={guideline?.footnotes}
          references={guideline?.references}
          onClose={() => setSelectedNodeId(null)}
          onNavigate={handleReferenceNavigate}
        />
      )}
    </>
  );
}

function FlowchartView() {
  return (
    <ReactFlowProvider>
      <FlowchartCanvas />
    </ReactFlowProvider>
  );
}

export default FlowchartView;
